import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { Dialog, UIPortalContext } from '@revelio/ui';

import { ChartNotSupported } from '../ChartNotSupported';
import styles from './expand-modal.module.css';

type ExpandModalContextValue = {
  open: (title: string) => void;
  setTitle: (title: string) => void;
  /** Marks a chart as mounted in the modal; returns the unmark. */
  trackChartMounted: () => () => void;
  /** The modal body while open; null while closed. */
  bodyEl: HTMLDivElement | null;
  /** The modal body while the chart is NOT mounted (its card is showing a
   * loader or an error instead); null otherwise. */
  fallbackEl: HTMLDivElement | null;
};

/**
 * Portals into the modal body. The Dialog offers itself as the portal target
 * (UIPortalContext) for popovers and tooltips, but only to its own children —
 * content portaled in from the card still carries the card's context, whose
 * target (document.body) paints under the modal. Re-provide the dialog.
 */
const portalIntoModal = (children: ReactNode, bodyEl: HTMLDivElement) =>
  createPortal(
    <UIPortalContext.Provider value={bodyEl.closest('dialog') ?? document.body}>
      {children}
    </UIPortalContext.Provider>,
    bodyEl
  );

const ExpandModalContext = createContext<ExpandModalContextValue | null>(null);

/**
 * Hosts a chart's Expand Plot modal above the chart card.
 *
 * The card cannot own the modal: whenever its data refetches — which every
 * header switcher change does — the card is swapped for a loader shell, and
 * anything inside it unmounts. So the open state, the title and the switchers
 * live here, and the card portals its current body into the modal: the chart
 * while it is mounted (`ExpandModalContent`), or the loader/error shown in its
 * place (`ExpandModalFallback`).
 */
export const ExpandModalProvider = ({
  controls,
  children,
}: {
  /** The card header's switchers, repeated above the expanded chart. */
  controls: ReactNode;
  children: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [bodyEl, setBodyEl] = useState<HTMLDivElement | null>(null);
  const [mountedCharts, setMountedCharts] = useState(0);
  const trackChartMounted = useCallback(() => {
    setMountedCharts((count) => count + 1);
    return () => setMountedCharts((count) => count - 1);
  }, []);

  const value = useMemo(
    () => ({
      open: (nextTitle: string) => {
        setTitle(nextTitle);
        setIsOpen(true);
      },
      setTitle,
      trackChartMounted,
      bodyEl,
      fallbackEl: mountedCharts > 0 ? null : bodyEl,
    }),
    [bodyEl, mountedCharts, trackChartMounted]
  );

  return (
    <ExpandModalContext.Provider value={value}>
      {children}
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title={title}
        size="6xl"
        className={styles.dialog}
        bodyClassName={styles.body}
      >
        {/* The dialog renders outside the card, so repeat what the card sets:
            `role="group"` for hover-revealed switchers (the sentiment metric
            switcher) and `translate="no"` against Chrome Translate rewriting
            chart text under React. */}
        <div role="group" translate="no" className={styles.content}>
          <div className={styles.controls}>{controls}</div>
          <div ref={setBodyEl} className={styles.chart} />
          {/* Shown by CSS only while nothing is portaled into the body: a card
              state that is neither the chart nor its loader/error shell. */}
          <div className={styles.empty}>
            <ChartNotSupported />
          </div>
        </div>
      </Dialog>
    </ExpandModalContext.Provider>
  );
};

/** Opens the chart's Expand Plot modal; null outside an ExpandModalProvider. */
export const useOpenExpandModal = () =>
  useContext(ExpandModalContext)?.open ?? null;

/** Renders the chart into the open modal and keeps its title current. */
export const ExpandModalContent = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  const expandModal = useContext(ExpandModalContext);
  const bodyEl = expandModal?.bodyEl ?? null;
  const setTitle = expandModal?.setTitle;
  const trackChartMounted = expandModal?.trackChartMounted;

  // The title follows the metric and segment switchers.
  useLayoutEffect(() => {
    if (bodyEl) setTitle?.(title);
  }, [bodyEl, title, setTitle]);

  // Layout effects so the handover with ExpandModalFallback settles before
  // paint when the card swaps between its chart and its loader.
  useLayoutEffect(() => {
    if (!bodyEl || !trackChartMounted) return;
    return trackChartMounted();
  }, [bodyEl, trackChartMounted]);

  return bodyEl ? portalIntoModal(children, bodyEl) : null;
};

/** Mirrors what the card shows in the chart's place (loader, error, no data)
 * into the open modal, only while the chart itself is not mounted. */
export const ExpandModalFallback = ({ children }: { children: ReactNode }) => {
  const fallbackEl = useContext(ExpandModalContext)?.fallbackEl ?? null;
  return fallbackEl ? portalIntoModal(children, fallbackEl) : null;
};
